import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { VOCAB_SETS, getCourses, B2_ASPEKTE_CHAPTERS } from '../data/vocab';

// Map special unicode characters to standard WinAnsi / Latin-1 characters
// supported by jsPDF standard fonts.
const CHAR_MAP = {
  '\u2E31': '\u00B7', // word separator dot -> ·
  '\u2E1A': '-\u00A8', // hyphen with diaeresis -> -¨
  '\u03B2': '\u00DF', // Greek beta -> ß
  '\u04E6': '\u00D6', // Cyrillic Ö -> Latin Ö
  '\u00A0': ' ',     // non-breaking space
  '\u2014': ' - ',   // em-dash -> -
  '\u2013': '-',     // en-dash -> -
  '\u2026': '...',   // ellipsis -> ...
  '\u2018': "'",
  '\u2019': "'",
  '\u201C': '"',
  '\u201D': '"',
};

function clean(str) {
  if (!str) return '';
  return String(str)
    .replace(/[\u2E31\u2E1A\u03B2\u04E6\u00A0\u2014\u2013\u2026\u2018\u2019\u201C\u201D]/g, (c) => CHAR_MAP[c] || c)
    .trim();
}

function buildSections(setId) {
  if (setId === 'b2') {
    return B2_ASPEKTE_CHAPTERS.map((chapter) => {
      const rows = [];
      chapter.modules.forEach((m) => {
        m.words.forEach((w) => rows.push({ de: clean(w.de), sq: clean(w.sq) }));
      });
      (chapter.phrases ?? []).forEach((p) => {
        rows.push({ de: clean(p.de), sq: clean(p.sq) });
      });
      return {
        title: clean(chapter.title || `Kapitel ${chapter.number}`),
        rows,
      };
    }).filter((s) => s.rows.length > 0);
  }

  const courses = getCourses(setId);
  return courses
    .flatMap((course) =>
      course.lessons.map((lesson) => ({
        title: courses.length > 1
          ? `${clean(course.level)} \u00B7 ${clean(lesson.title)}`
          : clean(lesson.title),
        rows: lesson.words.map((w) => ({ de: clean(w.de), sq: clean(w.sq) })),
      }))
    )
    .filter((s) => s.rows.length > 0);
}

/**
 * Generates and downloads a clean, beautifully formatted German-Albanian PDF
 * for the given level (A1, A2, B1, or B2) directly without opening the print dialog.
 */
export async function downloadVocabPdf(setId) {
  const info = VOCAB_SETS[setId] || {
    id: setId,
    level: setId.toUpperCase(),
    title: `Fjalori ${setId.toUpperCase()}`,
    book: 'Gjermanisht',
    unitsLabel: 'leksione',
  };

  const sections = buildSections(setId);
  const totalWords = sections.reduce((acc, s) => acc + s.rows.length, 0);

  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'pt',
    format: 'a4',
  });

  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 36;
  const contentWidth = pageWidth - margin * 2;

  // Header Banner: German Flag Accent (Black, Red, Gold)
  const drawBanner = () => {
    doc.setFillColor(24, 24, 27);
    doc.rect(0, 0, pageWidth, 5, 'F');
    doc.setFillColor(221, 0, 0);
    doc.rect(0, 5, pageWidth, 5, 'F');
    doc.setFillColor(255, 206, 0);
    doc.rect(0, 10, pageWidth, 5, 'F');
  };

  drawBanner();

  // Document Title
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(20);
  doc.setTextColor(24, 24, 27);
  doc.text(`${info.title} — Gjermanisht \u00B7 Shqip`, margin, 46);

  // Subtitle / Info
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(113, 113, 122);
  const metaText = `${info.book}  \u2022  ${sections.length} ${info.unitsLabel}  \u2022  ${totalWords} fjalë gjithsej`;
  doc.text(metaText, margin, 64);

  // Subtle separator line
  doc.setDrawColor(228, 228, 231);
  doc.setLineWidth(0.75);
  doc.line(margin, 74, pageWidth - margin, 74);

  let startY = 86;

  // Render each section as an autotable
  sections.forEach((section, sIdx) => {
    const tableBody = section.rows.map((row, rIdx) => [
      String(rIdx + 1),
      row.de,
      row.sq || '\u2014',
    ]);

    autoTable(doc, {
      startY,
      margin: { left: margin, right: margin, top: 40, bottom: 40 },
      head: [
        [
          {
            content: `${section.title} (${section.rows.length} fjalë)`,
            colSpan: 3,
            styles: {
              fillColor: [24, 24, 27],
              textColor: [255, 206, 0],
              fontSize: 10.5,
              fontStyle: 'bold',
              cellPadding: 5,
            },
          },
        ],
        ['#', 'Gjermanisht', 'Shqip'],
      ],
      body: tableBody,
      theme: 'grid',
      styles: {
        font: 'helvetica',
        fontSize: 9,
        cellPadding: 4.5,
        textColor: [39, 39, 42],
        lineColor: [228, 228, 231],
        lineWidth: 0.5,
        overflow: 'linebreak',
        valign: 'middle',
      },
      headStyles: {
        fillColor: [244, 244, 245],
        textColor: [24, 24, 27],
        fontStyle: 'bold',
        fontSize: 9,
      },
      alternateRowStyles: {
        fillColor: [250, 250, 250],
      },
      columnStyles: {
        0: { cellWidth: 28, halign: 'center', textColor: [161, 161, 170] },
        1: { cellWidth: (contentWidth - 28) * 0.48, fontStyle: 'bold' },
        2: { cellWidth: (contentWidth - 28) * 0.52 },
      },
      showHead: 'everyPage',
      didDrawPage: (data) => {
        // Redraw top flag strip on every page
        if (data.pageNumber > 1) {
          doc.setFillColor(24, 24, 27);
          doc.rect(0, 0, pageWidth, 4, 'F');
          doc.setFillColor(221, 0, 0);
          doc.rect(0, 4, pageWidth, 4, 'F');
          doc.setFillColor(255, 206, 0);
          doc.rect(0, 8, pageWidth, 4, 'F');
        }
      },
    });

    startY = doc.lastAutoTable.finalY + 16;
  });

  // Footer: page numbers on all pages
  const totalPages = doc.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(161, 161, 170);
    const footerText = `${info.title} \u00B7 Faqja ${i} nga ${totalPages}`;
    doc.text(footerText, pageWidth / 2, pageHeight - 18, { align: 'center' });
  }

  // Directly trigger download in user's browser
  const filename = `Fjalori-${info.level}-Gjermanisht-Shqip.pdf`;
  doc.save(filename);
}
